import { AlertTriangle } from "lucide-react"

import { cn } from "../../lib/utils"

// The underlined tab strip that splits a worklist into states — orders'
// `all | problem | resolved`, products' `problem | ok`. One component so the two
// pages cannot drift apart visually, and so a third worklist inherits the
// behaviour instead of forking the class list a third time.
export type StateToggleOption<T extends string> = {
  value: T
  label: string
  /** Server-computed count for this state. Never derive it from loaded rows —
   *  a number that grows as you scroll is not a state count. */
  count?: number
  /** `warning` marks the state that needs work: its count shows as a badge
   *  whenever it is above zero, on the active tab or not. Plain states show
   *  their count only while selected, so the strip stays quiet. */
  tone?: "default" | "warning"
  /** Title text for the warning badge, e.g. "3 unresolved problem orders". */
  countTitle?: string
}

export function StateToggle<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly StateToggleOption<T>[]
  value: T
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex items-stretch gap-5 self-stretch text-[13px] sm:self-auto"
    >
      {options.map((option) => {
        const isActive = option.value === value
        const isWarning = option.tone === "warning"
        const count = option.count
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isActive}
            onClick={() => onChange(option.value)}
            className={cn(
              "relative inline-flex min-h-9 items-center gap-1.5 pb-3 font-medium capitalize transition-colors duration-200 after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:origin-center after:rounded-full after:bg-foreground after:transition-transform after:duration-200",
              isActive
                ? "text-foreground after:scale-x-100"
                : "text-muted-foreground after:scale-x-0 hover:text-foreground"
            )}
          >
            {option.label}

            {isActive && !isWarning && count !== undefined ? (
              <span className="text-[11px] tabular-nums text-muted-foreground">{count}</span>
            ) : null}

            {isWarning && count !== undefined && count > 0 ? (
              <span
                className="inline-flex min-w-4 items-center justify-center gap-0.5 rounded-full bg-warning/15 px-1 text-xs font-medium leading-none text-warning tabular-nums"
                title={option.countTitle}
              >
                <AlertTriangle className="size-2.5" />

                {count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
